import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Eye, Edit2, Download } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface DocumentPreviewProps {
  content: string;
  isEditing: boolean;
  isLoading: boolean;
  onEdit: () => void;
  onSave: () => void;
  onContentChange: (content: string) => void;
  logoUrl?: string;
}

export function DocumentPreview({
  content,
  isEditing,
  isLoading,
  onEdit,
  onSave,
  onContentChange,
  logoUrl,
}: DocumentPreviewProps) {
  const isMobile = useIsMobile();
  
  if (!content) return null;

  const formatContent = (text: string) => {
    // Replace [LOGO] placeholder
    text = text.replace('[LOGO]', '');
    
    // Add proper spacing for sections
    text = text.replace(/(\d+\.|\-)\s/g, '\n$1 ');
    
    // Add spacing after paragraphs
    text = text.replace(/\.\s+/g, '.\n\n');
    
    return text.trim();
  };

  const handleDownload = () => {
    const element = document.createElement("a");
    const formattedContent = formatContent(content) + "\n\n\nLaget av Advokai";
    const file = new Blob([formattedContent], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = "dokument.txt";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <Card className="mt-6">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 border-b">
        <CardTitle className="text-xl">Forhåndsvis dokument</CardTitle>
        <img 
          src="/lovable-uploads/d5b7fb07-742a-44bd-933d-6ec76a27bd10.png"
          alt="Advokai Logo" 
          className="h-8 w-auto"
        />
      </CardHeader>
      <CardContent className="pt-6">
        <ScrollArea className="h-[600px] w-full rounded-md border p-6 bg-white">
          {isEditing ? (
            <Textarea
              value={content}
              onChange={(e) => onContentChange(e.target.value)}
              className="min-h-[550px] w-full font-mono text-sm border-none focus-visible:ring-0"
              placeholder="Rediger dokumentet her..."
              disabled={isLoading}
            />
          ) : (
            <div className="prose max-w-none whitespace-pre-wrap font-serif text-base leading-relaxed">
              {formatContent(content)}
              <div className="text-right text-sm text-gray-500 mt-8">
                Laget av Advokai
              </div>
            </div>
          )}
        </ScrollArea>
        <div className={`mt-6 grid ${isMobile ? 'grid-cols-1' : 'grid-cols-3'} gap-4`}>
          <Button 
            onClick={onEdit} 
            variant="outline"
            className="w-full flex items-center justify-center gap-2"
            disabled={isLoading}
          >
            {isEditing ? <Eye className="h-4 w-4" /> : <Edit2 className="h-4 w-4" />}
            {isEditing ? "Vis" : "Rediger"}
          </Button>
          <Button 
            onClick={onSave} 
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-2"
          >
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            {isLoading ? "Lagrer..." : "Lagre dokument"}
          </Button>
          <Button
            onClick={handleDownload}
            variant="outline"
            className="w-full flex items-center justify-center gap-2"
            disabled={isLoading}
          >
            <Download className="h-4 w-4" />
            Last ned
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}